import { format } from 'date-fns';

/**
 * Срез раздела «Сотрудники»: шапка раздела (сотрудники, отделы, фильтры по результатам
 * анализа) плюс направление из верхней панели. Один срез уходит во все запросы раздела —
 * и в графики `employees/*`, и в графики нод внизу, и в переход в журнал.
 *
 * Раньше у раздела был фильтр «Типы звонков» по legacy-колонке `call_classification_type`.
 * Движок нод её не пишет, поэтому список был пуст всегда, а направление из верхней панели
 * до раздела не доходило вовсе. Бизнес-тип звонка теперь выбирается фильтром по
 * результатам анализа — тем же, что в журнале.
 */
export const ALL = 'all';

/** Мультивыбор хранит `all` вместо пустого списка: «все» — это отсутствие фильтра. */
export const pickSelected = (selection) =>
  !Array.isArray(selection) || selection.includes(ALL)
    ? []
    : selection.filter((value) => value && value !== ALL);

export const buildEmployeeFilterParams = ({ dateRange, employees, departments, callType, nodeFilter } = {}) => {
  const employeeIds = pickSelected(employees);
  const selectedDepartments = pickSelected(departments);

  return {
    date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    // Один сотрудник уходит одиночным параметром: так KPI считает «звонков на сотрудника»
    // по нему одному, а не делит на число операторов в выборке.
    employee_id: employeeIds.length === 1 ? employeeIds[0] : undefined,
    employee_ids: employeeIds.length > 1 ? employeeIds : undefined,
    departments: selectedDepartments.length > 0 ? selectedDepartments : undefined,
    call_type: callType && callType !== ALL ? callType : undefined,
    node_filter: nodeFilter || undefined
  };
};

/**
 * Параметры графика качества: срез раздела плюс форма чек-листа, если она выбрана. Один
 * сборщик на динамику и сравнение качества — линия среднего на динамике обязана считаться
 * по той же форме, что и сами линии.
 */
export const withQualityForm = (params, form) =>
  form && form !== ALL ? { ...params, quality_form: form } : { ...params };

/**
 * Сужен ли раздел до части сотрудников. Среднее «по всем сотрудникам» фильтр не сужает,
 * и подпись обязана сказать это, когда фильтр выбран.
 */
export const hasEmployeeScope = ({ employees, departments } = {}) =>
  pickSelected(employees).length > 0 || pickSelected(departments).length > 0;

/**
 * Срез для графиков нод. Всегда списками: одиночный employee_id у них занят ячейкой
 * тепловой карты, которая открывает примеры своего оператора.
 */
export const buildEmployeeNodeScope = ({ employees, departments } = {}) => ({
  employeeIds: pickSelected(employees),
  departments: pickSelected(departments)
});

/**
 * Срез, который переход уносит в журнал: сотрудники, отделы и фильтры анализа. Сотрудники
 * и отделы — всегда списками, даже один: журнал держит их отдельно от своих селектов
 * и показывает чипами (см. readEmployeeScope в callDetailsFilters.js). Направление
 * не уезжает — оно в верхней панели, общей с журналом.
 */
export const buildEmployeeJournalScope = ({ employees, departments, nodeFilter } = {}) => {
  const employeeIds = pickSelected(employees);
  const selectedDepartments = pickSelected(departments);

  return {
    ...(employeeIds.length > 0 ? { employee_ids: employeeIds } : {}),
    ...(selectedDepartments.length > 0 ? { departments: selectedDepartments } : {}),
    ...(nodeFilter ? { node_filter: nodeFilter } : {})
  };
};
